// src/components/family/DeleteChildModal.tsx
// Confirmation modal for deleting a child profile

import { useEffect, useRef } from 'react';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import { useDeleteChild } from '@/features/children/hooks/useChildren';
import type { Child } from '@/features/children/types/childTypes';
import { getChildFullName, getChildInitials, getAvatarGradient } from '@/features/children/types/childTypes';

interface DeleteChildModalProps {
  isOpen: boolean;
  onClose: () => void;
  child: Child | null;
  onSuccess?: () => void;
}

export function DeleteChildModal({ isOpen, onClose, child, onSuccess }: DeleteChildModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const deleteChild = useDeleteChild();

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleDelete = async () => {
    if (!child) return;
    
    try {
      await deleteChild.mutateAsync(child.id);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to delete child:', error);
    }
  };

  if (!isOpen || !child) return null;

  const fullName = getChildFullName(child);
  const initials = getChildInitials(child);
  const avatarGradient = getAvatarGradient(child.first_name + child.last_name);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-navy/40 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-child-title"
        aria-describedby="delete-child-description"
        className="relative w-full max-w-md bg-white rounded-2xl shadow-lifted overflow-hidden animate-slideUp mx-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-warm-gray/10 bg-red-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <h2 id="delete-child-title" className="font-display font-semibold text-lg text-navy">
              Remove Child Profile
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-red-100 text-warm-gray hover:text-navy transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Child Preview */}
          <div className="flex items-center gap-4 p-4 bg-cream/50 rounded-xl mb-6">
            {child.avatar_url ? (
              <img
                src={child.avatar_url}
                alt={fullName}
                className="w-14 h-14 rounded-full object-cover"
              />
            ) : (
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center text-white font-display font-semibold text-lg"
                style={{ background: avatarGradient }}
              >
                {initials}
              </div>
            )}
            <div>
              <p className="font-display font-semibold text-navy">{fullName}</p>
              <p className="text-sm text-warm-gray">
                {child.diagnoses?.length
                  ? `${child.diagnoses.length} diagnosis${child.diagnoses.length > 1 ? 'es' : ''}`
                  : 'No diagnoses'}
              </p>
            </div>
          </div>

          {/* Warning Message */}
          <p id="delete-child-description" className="text-navy leading-relaxed mb-4">
            Are you sure you want to remove <strong>{child.first_name}</strong>'s profile? This action will permanently delete:
          </p>

          <ul className="space-y-2 mb-6">
            <li className="flex items-center gap-2 text-sm text-warm-gray">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              All health tracking data and logs
            </li>
            <li className="flex items-center gap-2 text-sm text-warm-gray">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              Associated documents and records
            </li>
            <li className="flex items-center gap-2 text-sm text-warm-gray">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              Goals, appointments, and care team info
            </li>
            <li className="flex items-center gap-2 text-sm text-warm-gray">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              All expense records linked to this child
            </li>
          </ul>

          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 mb-6">
            <p className="text-sm text-amber-800">
              <strong>This action cannot be undone.</strong> Consider exporting your data first if you need to keep records.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-warm-gray/10 bg-cream/30">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-navy hover:bg-cream transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteChild.isPending}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {deleteChild.isPending ? (
              <>
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Removing...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Remove Profile
              </>
            )}
          </button>
        </div>

        {/* Error Display */}
        {deleteChild.isError && (
          <div className="absolute bottom-20 left-6 right-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-700">
              {deleteChild.error instanceof Error
                ? deleteChild.error.message
                : 'Failed to remove child. Please try again.'}
            </p>
          </div>
        )}
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px) scale(0.98);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
    </div>
  );
}

export default DeleteChildModal;

